import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { sessaoAtiva, tokenUtilizavel } from '@/nucleo/auth/armazem-token';
import { chamarApi, ErroApi, renovarUmaVezSo } from '@/nucleo/auth/cliente-api';
import { lerConfiguracao } from '@/nucleo/auth/configuracao';
import { somenteDigitos } from '@/nucleo/formato/documentos';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import {
  API_PORTAL,
  TAMANHO_MAXIMO_BYTES,
  TIPOS_ACEITOS,
  type DocumentoEntrada,
  type DossieDetalhe,
  type DossieResumo,
  type ProfissionalEntrada,
} from '@/modulos/operacional/pre-credenciamento/contrato';

/**
 * Camada de dados da documentação do pré-credenciado (frente F10, D-28), lado
 * do portal. Dado real, da API `bff/v1/documentacao-pre-credenciado/portal`;
 * quem é o dono do dossiê vem do token, e toda regra (interesse confirmado,
 * validade, idade, CNH, substituição) é conferida no banco (0248). Nenhum mock.
 */

export const CHAVES = {
  tudo: ['pre-credenciamento-portal'] as const,
  lista: ['pre-credenciamento-portal', 'lista'] as const,
  dossie: (id: string) => ['pre-credenciamento-portal', 'dossie', id] as const,
};

const doDossie = (id: string) => `${API_PORTAL}/dossies/${encodeURIComponent(id)}`;

export function useMeusDossies() {
  return useQuery({
    queryKey: CHAVES.lista,
    queryFn: () => chamarApi<{ itens: DossieResumo[] }>(`${API_PORTAL}/dossies`),
    retry: naoInsistirEmRecusa,
  });
}

export function useDossie(id: string) {
  return useQuery({
    queryKey: CHAVES.dossie(id),
    queryFn: () => chamarApi<DossieDetalhe>(doDossie(id)),
    retry: (tentativas, erro) =>
      !(erro instanceof ErroApi && erro.situacao === 404) && naoInsistirEmRecusa(tentativas, erro),
  });
}

/**
 * Toda gravação devolve o dossiê atualizado: ele entra no lugar do que estava
 * na memória, e a lista é refeita (o progresso dela mudou).
 */
function useGravacao<Entrada>(id: string, enviar: (entrada: Entrada) => Promise<DossieDetalhe>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: enviar,
    onSuccess: (dossie) => {
      cliente.setQueryData(CHAVES.dossie(id), dossie);
      void cliente.invalidateQueries({ queryKey: CHAVES.lista });
    },
  });
}

export type FuncaoDoProfissional = 'condutor' | 'monitor';

export function useSalvarProfissional(id: string, funcao: FuncaoDoProfissional) {
  return useGravacao(id, (entrada: ProfissionalEntrada) =>
    chamarApi<DossieDetalhe>(`${doDossie(id)}/profissionais/${funcao}`, {
      metodo: 'PUT',
      corpo: entrada,
    }),
  );
}

export function useEnviarDocumento(id: string) {
  return useGravacao(id, (entrada: DocumentoEntrada) =>
    chamarApi<DossieDetalhe>(`${doDossie(id)}/documentos`, { metodo: 'POST', corpo: entrada }),
  );
}

/** Chave de idempotência: uma por tentativa consciente (uma por abertura de diálogo). */
export function novaChave(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Mensagem da API quando ela disse o que fazer (409, 422…); senão, a genérica. */
export function mensagemDoErro(erro: unknown, generica: string): string {
  return erro instanceof ErroApi && erro.message ? erro.message : generica;
}

// ---------------------------------------------------------------------
// Profissional
// ---------------------------------------------------------------------

/** Os seis dígitos do meio do CPF mascarado pela API (`***.456.789-**`), ou null. */
function digitosDoMeio(cpfMascarado: string): string | null {
  const achado = /^\*{3}\.(\d{3})\.(\d{3})-\*{2}$/.exec(cpfMascarado);
  return achado ? `${achado[1]}${achado[2]}` : null;
}

/** Provavelmente a mesma pessoa: os seis dígitos visíveis dos dois CPFs mascarados batem. */
export function mesmoCpfMascarado(um: string, outro: string): boolean {
  const meio = digitosDoMeio(um);
  return meio !== null && meio === digitosDoMeio(outro);
}

/** Provavelmente a mesma pessoa: os dígitos visíveis do CPF atual batem com os digitados. */
export function mesmoCpf(cpfMascarado: string, cpfDigitado: string): boolean {
  const meio = digitosDoMeio(cpfMascarado);
  return meio !== null && somenteDigitos(cpfDigitado).slice(3, 9) === meio;
}

// ---------------------------------------------------------------------
// Arquivo
// ---------------------------------------------------------------------

const EXTENSOES_ACEITAS = ['pdf', 'jpg', 'jpeg', 'png'];

export function formatarTamanho(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

/**
 * O que certamente seria recusado pelo banco (tipo e tamanho) é barrado aqui,
 * antes de ler e mandar 5 MB pela rede. Devolve a frase para o usuário, ou null.
 */
export function problemaDoArquivo(arquivo: File): string | null {
  const extensao = arquivo.name.split('.').pop()?.toLowerCase() ?? '';
  const tipoAceito =
    (TIPOS_ACEITOS as readonly string[]).includes(arquivo.type) ||
    (!arquivo.type && EXTENSOES_ACEITAS.includes(extensao));
  if (!tipoAceito || !EXTENSOES_ACEITAS.includes(extensao)) {
    return 'Envie o documento em PDF, JPG ou PNG.';
  }
  if (arquivo.size === 0) {
    return 'O arquivo está vazio. Escolha outro arquivo.';
  }
  if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
    return `O arquivo tem ${formatarTamanho(arquivo.size)} e o limite é ${formatarTamanho(
      TAMANHO_MAXIMO_BYTES,
    )}. Reduza o tamanho (por exemplo, digitalizando em resolução menor) e tente de novo.`;
  }
  return null;
}

/** Lê o arquivo no navegador e devolve o conteúdo em base64, sem o prefixo `data:`. */
export function lerComoBase64(arquivo: File): Promise<string> {
  return new Promise((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => {
      const texto = typeof leitor.result === 'string' ? leitor.result : '';
      const virgula = texto.indexOf(',');
      resolver(virgula >= 0 ? texto.slice(virgula + 1) : texto);
    };
    leitor.onerror = () => rejeitar(leitor.error ?? new Error('Falha ao ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

/**
 * Abre o arquivo enviado numa aba nova. `chamarApi` só lê JSON, e o arquivo
 * precisa do mesmo cabeçalho de autorização (mesmo caminho de
 * `admin/ciclo-inscricao/dados.ts`, `baixarEdital`). A aba é aberta antes da
 * espera, no próprio clique, para o navegador não a tratar como janela não
 * pedida; se ela não abrir, o arquivo é baixado.
 */
export async function abrirArquivo(documentoId: string, nome: string): Promise<void> {
  const janela = window.open('', '_blank');
  if (janela) janela.opener = null;
  try {
    if (!tokenUtilizavel()) await renovarUmaVezSo();
    const sessao = sessaoAtiva();
    const resposta = await fetch(
      `${lerConfiguracao().api}${API_PORTAL}/documentos/${encodeURIComponent(documentoId)}/arquivo`,
      {
        headers: sessao ? { Authorization: `Bearer ${sessao.acesso}` } : {},
        credentials: 'omit',
      },
    );
    if (!resposta.ok) {
      throw new ErroApi(resposta.status, 'Não foi possível abrir o arquivo. Tente de novo.');
    }
    const endereco = URL.createObjectURL(await resposta.blob());
    if (janela) {
      janela.location.href = endereco;
    } else {
      const link = document.createElement('a');
      link.href = endereco;
      link.download = nome;
      link.click();
    }
    // A aba nova precisa do endereço vivo enquanto carrega.
    window.setTimeout(() => URL.revokeObjectURL(endereco), 60_000);
  } catch (erro) {
    janela?.close();
    throw erro instanceof ErroApi
      ? erro
      : new ErroApi(0, 'Não foi possível abrir o arquivo. Tente de novo.');
  }
}
